import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { botApi, BotPendingSurveyItem } from '../../api/admin.api';
import { Card, CardContent } from '../ui/card';
import { BotPendingRow, PENDING_COLUMNS } from './BotPendingRow';
import { Clock } from 'lucide-react';

/** 목록 조회 기간 (AC-41) */
const PENDING_LIST_DAYS = 14;
/** 하루 다시 만들기 상한 — 서버 BotApprovalFacade.MAX_REGENERATE_PER_DAY 와 같은 값 (spec §4.9.5 · AC-36) */
const MAX_REGENERATE_PER_DAY = 3;
/** 그날 첫 생성분 1건 — 다시 만들기 횟수 = 그날 행 수 - 1 */
const FIRST_GENERATION_COUNT = 1;
/** 다시 만들기 가능 대상일 — 오늘·어제만 (서버 울타리와 같은 폭) */
const REGENERATABLE_DAYS_BACK = 1;

const APPROVE_CONFIRM = (description: string) =>
  `이 설문을 지금 발행할까요?\n보이는 문장 그대로 올라가요. 발행은 되돌릴 수 없어요.\n\n「${description}」`;
const REJECT_CONFIRM = '이 설문을 반려할까요?\n반려하면 다시 만들기로 새 설문을 받을 수 있어요.';

type Notice = { tone: 'success' | 'error'; text: string };

const getErrorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

/**
 * 🔴 KST 고정 yyyy-MM-dd — 서버 울타리가 DateTimeUtils.today()(Asia/Seoul) 라서 브라우저 TZ 를 따라가면 안 된다.
 * 브라우저가 KST 가 아니면 버튼이 사라지거나, 서버가 409 를 던질 행에 버튼이 보인다 (spec §4.9.3 R4-2)
 */
const KST_DATE_FORMAT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' });
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getRegeneratableDates = () => {
  const dates = new Set<string>();
  const now = Date.now();
  for (let back = 0; back <= REGENERATABLE_DAYS_BACK; back++) {
    dates.add(KST_DATE_FORMAT.format(new Date(now - back * MS_PER_DAY)));
  }
  return dates;
};

/**
 * 트랙 B 「자동발행 대기」 탭 — 승인 · 반려 · 다시 만들기 (spec §4.9.4 · 4.9.5 · 4.9.7)
 * 🔴 원문 수정 UI 없음 — 승인 = 보이는 그대로 게시
 */
export const BotPendingSurveyPanel = () => {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<Notice | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['botPending'],
    queryFn: () => botApi.getPending(PENDING_LIST_DAYS).then((r) => r.data),
  });
  const items = useMemo(() => data?.items ?? [], [data]);

  /* 날짜별 행 수 + 그날 마지막(attemptNo 최대) 행 id */
  const { countByDate, lastIdByDate } = useMemo(() => {
    const counts = new Map<string, number>();
    const lasts = new Map<string, BotPendingSurveyItem>();
    items.forEach((item) => {
      counts.set(item.targetDate, (counts.get(item.targetDate) ?? 0) + 1);
      const last = lasts.get(item.targetDate);
      if (!last || item.attemptNo > last.attemptNo) lasts.set(item.targetDate, item);
    });
    const lastIds = new Map<string, number>();
    lasts.forEach((item, date) => lastIds.set(date, item.id));
    return { countByDate: counts, lastIdByDate: lastIds };
  }, [items]);

  /* 재조회 Promise 를 돌려줘서 isPending 이 재조회까지 덮게 한다 — 옛 상태로 버튼이 다시 켜지지 않도록 */
  const refresh = (keys: string[] = []) =>
    Promise.all(
      ['botPending', 'botStatus', ...keys].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
    );

  /* 승인은 bot_topic_history 1행 + SURVEY_CREATE 로그 1행을 만든다 — 「봇 관리」 탭 캐시도 같이 비운다 */
  const PUBLISH_AFFECTED_KEYS = ['botTopicHistory', 'botLogs'];

  /* 409(이미 처리) · 422(실패 사유 저장) 모두 행 상태가 바뀌었을 수 있어 실패에도 다시 불러온다 */
  const handleError = (err: unknown, fallback: string) => {
    setNotice({ tone: 'error', text: getErrorMessage(err, fallback) });
    return refresh();
  };

  const approveMutation = useMutation({
    mutationFn: (id: number) => botApi.approvePending(id).then((r) => r.data),
    onSuccess: (result) => {
      setNotice({
        tone: 'success',
        text: result.questionId ? `발행했어요. (설문 #${result.questionId})` : '발행했어요.',
      });
      return refresh(PUBLISH_AFFECTED_KEYS);
    },
    onError: (err) => handleError(err, '발행하지 못했어요. 다시 시도해 주세요.'),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) => botApi.rejectPending(id).then((r) => r.data),
    onSuccess: (_result, id) => {
      const remaining = remainingAfterReject(id);
      setNotice({
        tone: 'success',
        text: remaining > 0 ? `반려했어요. 다시 만들기를 ${remaining}번 더 쓸 수 있어요.` : '반려했어요. 오늘은 다시 만들기를 다 썼어요.',
      });
      return refresh();
    },
    onError: (err) => handleError(err, '반려하지 못했어요. 다시 시도해 주세요.'),
  });

  const regenerateMutation = useMutation({
    mutationFn: (date: string) => botApi.regeneratePending(date).then((r) => r.data),
    onSuccess: (result) => {
      setNotice({ tone: 'success', text: result.message || '새 설문을 만들었어요.' });
      return refresh();
    },
    onError: (err) => handleError(err, '새 설문을 만들지 못했어요. 다시 시도해 주세요.'),
  });

  /* ── 핸들러 ── */
  /* 🔴 승인은 되돌릴 수 없다 → 확인 단계 필수 (spec §4.9.7) */
  const handleApprove = (item: BotPendingSurveyItem) => {
    if (!window.confirm(APPROVE_CONFIRM(item.description))) return;
    setNotice(null);
    approveMutation.mutate(item.id);
  };

  const handleReject = (item: BotPendingSurveyItem) => {
    if (!window.confirm(REJECT_CONFIRM)) return;
    setNotice(null);
    rejectMutation.mutate(item.id);
  };

  const handleRegenerate = (item: BotPendingSurveyItem) => {
    setNotice(null);
    regenerateMutation.mutate(item.targetDate);
  };

  /* ── 행 단위 판단 ── */
  const regeneratableDates = useMemo(getRegeneratableDates, []);

  /** 반려한 행의 그날 기준 남은 다시 만들기 횟수 (성공 안내 문구용) */
  const remainingAfterReject = (id: number) => {
    const target = items.find((item) => item.id === id);
    if (!target) return 0;
    const used = (countByDate.get(target.targetDate) ?? FIRST_GENERATION_COUNT) - FIRST_GENERATION_COUNT;
    return Math.max(MAX_REGENERATE_PER_DAY - used, 0);
  };

  /** 그날 마지막 행이 반려이고 오늘·어제일 때만 남은 횟수, 아니면 null (버튼 없음) */
  const getRemainingRegenerate = (item: BotPendingSurveyItem) => {
    if (item.status !== 'REJECTED') return null;
    if (lastIdByDate.get(item.targetDate) !== item.id) return null;
    if (!regeneratableDates.has(item.targetDate)) return null;
    const used = (countByDate.get(item.targetDate) ?? FIRST_GENERATION_COUNT) - FIRST_GENERATION_COUNT;
    return MAX_REGENERATE_PER_DAY - used;
  };

  const isRegeneratingRow = (item: BotPendingSurveyItem) =>
    regenerateMutation.isPending &&
    regenerateMutation.variables === item.targetDate &&
    lastIdByDate.get(item.targetDate) === item.id;

  const isRowBusy = (item: BotPendingSurveyItem) =>
    (approveMutation.isPending && approveMutation.variables === item.id) ||
    (rejectMutation.isPending && rejectMutation.variables === item.id) ||
    isRegeneratingRow(item);

  const summary = data?.summary;
  const summaryItems = [
    { label: '승인', value: summary?.approved, className: 'text-green-600' },
    { label: '반려', value: summary?.rejected, className: 'text-red-600' },
    { label: '만료', value: summary?.expired, className: 'text-gray-500' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          자동발행 대기
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          트랙 B 봇이 만든 설문을 확인하고 발행해요. 문장은 고칠 수 없고, 승인하거나 반려만 할 수 있어요.
        </p>
      </div>

      {/* ── 상단 요약 (AC-41) ── */}
      <Card className="p-4">
        <CardContent className="p-0 space-y-3">
          <div>
            <div className="text-sm font-semibold text-gray-900">최근 7일 · 20:30 첫 생성분</div>
            <div className="text-xs text-gray-500 mt-0.5">
              자동 발행으로 바꿀지 정할 때 봐요. 다시 만든 설문은 세지 않아요.
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {summaryItems.map((s) => (
              <div key={s.label} className="rounded-lg bg-gray-50 px-4 py-3">
                <div className="text-xs text-gray-500">{s.label}</div>
                <div className={`text-2xl font-bold ${s.className}`}>{s.value ?? '—'}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {notice && (
        <div
          role="alert"
          aria-live="polite"
          className={`rounded-lg px-4 py-3 text-sm ${
            notice.tone === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
        >
          {notice.text}
        </div>
      )}

      {/* ── 대기 목록 (최근 14일) ── */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">로딩 중...</div>
          ) : isError ? (
            <div className="text-center py-8 text-red-600">목록을 불러오지 못했어요. 새로고침해 주세요.</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              최근 {PENDING_LIST_DAYS}일 동안 만든 대기 설문이 없어요
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    {PENDING_COLUMNS.map((col) => (
                      <th key={col.label} scope="col" className={`${col.align} px-4 py-3 font-medium text-gray-600`}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <BotPendingRow
                      key={item.id}
                      item={item}
                      remainingRegenerate={getRemainingRegenerate(item)}
                      busy={isRowBusy(item)}
                      isRegenerating={isRegeneratingRow(item)}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onRegenerate={handleRegenerate}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
